import AbstractApiService from 'src/services/abstractApiService';
import { api } from 'pages/APIs';
import type {
  TeamsIntegration,
  TeamsIntegrationCreate,
  TeamsIntegrationUpdate,
  TeamsListResponse,
  TeamsSingleResponse,
  TeamsTestResponse,
  TeamsDeleteResponse,
} from 'src/@types/teams';

/**
 * API Service for managing Microsoft Teams channel integrations via doc2-api.
 */
export default class TeamsApiService extends AbstractApiService {
  public constructor() {
    super({});
  }

  /**
   * List all Teams integrations for the organization.
   */
  public async list(): Promise<TeamsListResponse> {
    return super.get(api.teams_list);
  }

  /**
   * Get a single Teams integration by ID.
   */
  public async getOne(id: string): Promise<TeamsSingleResponse> {
    return super.get(`${api.teams_get}${id}`);
  }

  /**
   * Create a new Teams integration.
   */
  public async create(
    data: TeamsIntegrationCreate
  ): Promise<TeamsSingleResponse> {
    return super.post(api.teams_create, data);
  }

  /**
   * Update an existing Teams integration.
   */
  public async update(
    id: string,
    data: TeamsIntegrationUpdate
  ): Promise<TeamsSingleResponse> {
    return super.put(`${api.teams_update}${id}`, data);
  }

  /**
   * Delete a Teams integration.
   */
  public async remove(id: string): Promise<TeamsDeleteResponse> {
    return super.delete(`${api.teams_delete}${id}`, {});
  }

  /**
   * Test a Teams integration by sending a test message.
   */
  public async test(id: string): Promise<TeamsTestResponse> {
    return super.post(`${api.teams_test}${id}/test`, {});
  }

  // =====================================================
  // Alias methods for consistency
  // =====================================================

  /**
   * Get all Teams integrations (alias for list, returns array).
   */
  public async getAll(): Promise<TeamsIntegration[]> {
    const result = await this.list();
    return result.data || [];
  }

  /**
   * Get integration by ID (alias for getOne).
   */
  public async getById(id: string): Promise<TeamsIntegration> {
    const result = await this.getOne(id);
    return result.data;
  }

  /**
   * Delete integration (alias for remove).
   */
  public async deleteIntegration(id: string): Promise<void> {
    await this.remove(id);
  }

  /**
   * Toggle integration enabled status.
   */
  public async toggle(
    id: string,
    enabled: boolean
  ): Promise<TeamsSingleResponse> {
    return this.update(id, { is_enabled: enabled });
  }
}
